import React, { useState, useEffect } from 'react'
import {
  Card,
  Button,
  Space,
  message,
  Tag,
  Typography,
  Select,
  DatePicker,
  Input,
  Statistic,
  Row,
  Col,
  Modal,
  Dropdown
} from 'antd'
import {
  SearchOutlined,
  ReloadOutlined,
  DollarOutlined,
  StopOutlined,
  RollbackOutlined,
  ExclamationCircleOutlined
} from '@ant-design/icons'
import api from '../../utils/api'
import styled from 'styled-components'
import AdminTable from '../../components/common/AdminTable'
import dayjs from 'dayjs'

const { Title } = Typography
const { Search } = Input
const { RangePicker } = DatePicker
const { Option } = Select

// 莫兰迪色系配色
const colors = {
  cardBackground: '#faf9f6',
  border: 'rgba(168, 181, 192, 0.3)',
  shadow: 'rgba(154, 163, 175, 0.2)',
  textPrimary: '#5a5a5a',
  textSecondary: '#8a8a8a'
}

const StyledCard = styled(Card)`
  background: ${colors.cardBackground};
  border-radius: 16px;
  box-shadow: 0 4px 16px ${colors.shadow};
  border: 1px solid ${colors.border};
  width: 100%;
  box-sizing: border-box;
  overflow-x: hidden;
  margin-bottom: 20px;

  .ant-card-body {
    padding: 24px;
    width: 100%;
    box-sizing: border-box;
    overflow-x: hidden;

    @media (max-width: 768px) {
      padding: 16px 12px;
    }
  }

  @media (max-width: 768px) {
    border-radius: 12px;
    margin-bottom: 16px;
  }
`

const StatsCard = styled(Card)`
  background: ${colors.cardBackground};
  border-radius: 16px;
  box-shadow: 0 4px 16px ${colors.shadow};
  border: 1px solid ${colors.border};
  margin-bottom: 20px;

  .ant-card-body {
    padding: 20px;
  }

  @media (max-width: 768px) {
    border-radius: 12px;
    margin-bottom: 16px;
  }
`

const FilterContainer = styled.div`
  margin-bottom: 20px;
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  align-items: flex-end;
  width: 100%;
  box-sizing: border-box;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 10px;
  }
`

const FilterItem = styled.div`
  flex: 1;
  min-width: 200px;

  @media (max-width: 768px) {
    width: 100%;
    min-width: 100%;
  }
`

const CreditPaymentsPage = () => {
  const [loading, setLoading] = useState(false)
  const [data, setData] = useState([])
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0
  })
  const [stats, setStats] = useState({
    totalAmount: 0,
    completedAmount: 0,
    totalCount: 0,
    completedCount: 0
  })
  const [filters, setFilters] = useState({
    search: '',
    status: undefined,
    startDate: undefined,
    endDate: undefined
  })

  // 获取交易记录
  const fetchData = async (page = 1, pageSize = 20) => {
    setLoading(true)
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: pageSize.toString()
      })

      // 添加搜索参数
      if (filters.search && filters.search.trim()) {
        params.append('search', filters.search.trim())
      }

      // 添加状态筛选
      if (filters.status) {
        params.append('status', filters.status)
      }

      // 添加日期筛选
      if (filters.startDate) {
        params.append('startDate', filters.startDate)
      }
      if (filters.endDate) {
        params.append('endDate', filters.endDate)
      }

      const response = await api.get(
        `/api/admin/credit-payments?${params.toString()}`
      )

      if (response.data.success) {
        setData(response.data.data || [])
        setPagination({
          current: response.data.pagination?.currentPage || page,
          pageSize: response.data.pagination?.itemsPerPage || pageSize,
          total: response.data.pagination?.totalItems || 0
        })
        setStats(response.data.stats || {
          totalAmount: 0,
          completedAmount: 0,
          totalCount: 0,
          completedCount: 0
        })
      } else {
        message.error('獲取交易記錄失敗')
      }
    } catch (error) {
      console.error('獲取交易記錄錯誤:', error)
      message.error('獲取交易記錄失敗')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData(pagination.current, pagination.pageSize)
  }, [filters])

  // 处理搜索
  const handleSearch = (value) => {
    setFilters((prev) => ({ ...prev, search: value }))
    setPagination((prev) => ({ ...prev, current: 1 }))
  }

  // 处理状态筛选
  const handleStatusChange = (value) => {
    setFilters((prev) => ({ ...prev, status: value }))
    setPagination((prev) => ({ ...prev, current: 1 }))
  }

  // 处理日期筛选
  const handleDateRangeChange = (dates) => {
    if (dates && dates.length === 2) {
      setFilters((prev) => ({
        ...prev,
        startDate: dates[0].format('YYYY-MM-DD'),
        endDate: dates[1].format('YYYY-MM-DD')
      }))
    } else {
      setFilters((prev) => ({
        ...prev,
        startDate: undefined,
        endDate: undefined
      }))
    }
    setPagination((prev) => ({ ...prev, current: 1 }))
  }

  // 清除筛选
  const handleClearFilters = () => {
    setFilters({
      search: '',
      status: undefined,
      startDate: undefined,
      endDate: undefined
    })
    setPagination((prev) => ({ ...prev, current: 1 }))
  }

  // 处理表格变化
  const handleTableChange = (newPagination) => {
    setPagination(newPagination)
    fetchData(newPagination.current, newPagination.pageSize)
  }

  // 格式化金额
  const formatAmount = (amount) => {
    return new Intl.NumberFormat('zh-TW', {
      style: 'currency',
      currency: 'TWD',
      minimumFractionDigits: 0
    }).format(amount)
  }

  // 状态标签
  const getStatusTag = (status) => {
    const statusConfig = {
      pending: { color: 'gold', text: '待處理' },
      completed: { color: 'green', text: '已完成' },
      failed: { color: 'red', text: '失敗' },
      refunded: { color: 'orange', text: '已退款' }
    }
    const config = statusConfig[status] || { color: 'default', text: status }
    return <Tag color={config.color}>{config.text}</Tag>
  }

  // 支付类型标签
  const getPaymentForTag = (paymentFor) => {
    const typeConfig = {
      booking: { color: 'blue', text: '預約' },
      product: { color: 'purple', text: '商品' },
      quick_payment: { color: 'cyan', text: '快速付款' }
    }
    const config = typeConfig[paymentFor] || {
      color: 'default',
      text: paymentFor
    }
    return <Tag color={config.color}>{config.text}</Tag>
  }

  // ── 信用卡操作 ──────────────────────────────────
  const [actionLoading, setActionLoading] = useState({})

  const handleCancelAuth = async (record) => {
    setActionLoading((prev) => ({ ...prev, [record._id]: 'cancel' }))
    try {
      const response = await api.post('/api/admin/credit-card/cancel', {
        order_sn: record.order_sn,
        trade_no: record.trade_no,
        amount: record.amount,
        business_id: record.business?.id?._id || record.business?.id
      })
      if (response.data.success) {
        message.success(response.data.message || '取消授權成功')
        fetchData(pagination.current, pagination.pageSize)
      } else {
        message.error(response.data.message || '取消授權失敗')
      }
    } catch (error) {
      console.error('取消授權失敗:', error)
      message.error('取消授權失敗')
    } finally {
      setActionLoading((prev) => ({ ...prev, [record._id]: null }))
    }
  }

  const handleRefund = async (record) => {
    if (!record.trade_no) {
      message.error('缺少藍新交易序號，無法退款')
      return
    }
    setActionLoading((prev) => ({ ...prev, [record._id]: 'refund' }))
    try {
      const response = await api.post('/api/admin/credit-card/close', {
        order_sn: record.order_sn,
        trade_no: record.trade_no,
        amount: record.amount,
        close_type: 2,
        business_id: record.business?.id?._id || record.business?.id
      })
      if (response.data.success) {
        message.success(response.data.message || '退款成功')
        fetchData(pagination.current, pagination.pageSize)
      } else {
        message.error(response.data.message || '退款失敗')
      }
    } catch (error) {
      console.error('退款失敗:', error)
      message.error('退款失敗')
    } finally {
      setActionLoading((prev) => ({ ...prev, [record._id]: null }))
    }
  }

  const handleQueryTrade = async (record) => {
    setActionLoading((prev) => ({ ...prev, [record._id]: 'query' }))
    try {
      const response = await api.post('/api/admin/credit-card/query', {
        order_sn: record.order_sn,
        trade_no: record.trade_no,
        amount: record.amount,
        business_id: record.business?.id?._id || record.business?.id
      })
      if (response.data.success) {
        const d = response.data.data
        Modal.info({
          title: '交易查詢結果',
          width: 520,
          content: (
            <div style={{ marginTop: 12 }}>
              <p><b>交易狀態：</b>{d.TradeStatus === '1' ? '已付款' : d.TradeStatus === '0' ? '未付款' : d.TradeStatus}</p>
              <p><b>交易序號：</b>{d.TradeNo || '-'}</p>
              <p><b>金額：</b>NT$ {d.Amt || record.amount}</p>
              <p><b>付款方式：</b>{d.PaymentType || '-'}</p>
              <p><b>付款時間：</b>{d.PayTime || '-'}</p>
              <p><b>授權碼：</b>{d.Auth || '-'}</p>
              <p><b>請款狀態：</b>{d.CloseStatus === '0' ? '未請款' : d.CloseStatus === '3' ? '已請款' : d.CloseStatus || '-'}</p>
              <p><b>退款狀態：</b>{d.BackStatus === '0' ? '未退款' : d.BackStatus === '3' ? '已退款' : d.BackStatus || '-'}</p>
            </div>
          )
        })
      } else {
        message.error(response.data.message || '查詢失敗')
      }
    } catch (error) {
      console.error('查詢交易失敗:', error)
      message.error('查詢交易失敗')
    } finally {
      setActionLoading((prev) => ({ ...prev, [record._id]: null }))
    }
  }

  // 表格列定义
  const columns = [
    {
      title: '訂單號',
      dataIndex: 'order_sn',
      key: 'order_sn',
      render: (text) => (
        <span style={{ fontFamily: 'monospace', fontSize: '13px' }}>
          {text}
        </span>
      )
    },
    {
      title: '支付類型',
      dataIndex: 'payment_for',
      key: 'payment_for',
      render: (paymentFor) => getPaymentForTag(paymentFor)
    },
    {
      title: '商家名稱',
      dataIndex: ['business', 'name'],
      key: 'businessName',
      render: (text, record) => text || record.business?.username || '-'
    },
    {
      title: '客戶姓名',
      dataIndex: ['customer', 'name'],
      key: 'customerName',
      render: (text) => text || '-'
    },
    {
      title: '客戶電話',
      dataIndex: ['customer', 'phone'],
      key: 'customerPhone',
      render: (text) => text || '-'
    },
    {
      title: '金額',
      dataIndex: 'amount',
      key: 'amount',
      render: (amount) => (
        <span style={{ fontWeight: 500, color: colors.textPrimary }}>
          {formatAmount(amount)}
        </span>
      )
    },
    {
      title: '狀態',
      dataIndex: 'status',
      key: 'status',
      render: (status) => getStatusTag(status)
    },
    {
      title: '交易號',
      dataIndex: 'trade_no',
      key: 'trade_no',
      render: (text) =>
        text ? (
          <span style={{ fontFamily: 'monospace', fontSize: '12px' }}>
            {text}
          </span>
        ) : (
          '-'
        )
    },
    {
      title: '卡號後四位',
      dataIndex: 'card_last_four',
      key: 'card_last_four',
      render: (text) =>
        text ? (
          <span style={{ fontFamily: 'monospace' }}>**** {text}</span>
        ) : (
          '-'
        )
    },
    {
      title: '建立時間',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date) =>
        date ? dayjs(date).format('YYYY-MM-DD HH:mm') : '-'
    },
    {
      title: '支付時間',
      dataIndex: 'pay_time',
      key: 'pay_time',
      render: (date) => (date ? dayjs(date).format('YYYY-MM-DD HH:mm') : '-')
    },
    {
      title: '操作',
      key: 'actions',
      fixed: 'right',
      width: 100,
      render: (_, record) => {
        const loading = actionLoading[record._id]
        const menuItems = [
          {
            key: 'query',
            icon: <SearchOutlined />,
            label: '查詢交易狀態',
            onClick: () => handleQueryTrade(record)
          }
        ]
        if (record.status === 'completed') {
          menuItems.push(
            {
              key: 'cancel',
              icon: <StopOutlined />,
              label: '取消授權',
              danger: true,
              onClick: () => {
                Modal.confirm({
                  title: '確認取消授權',
                  icon: <ExclamationCircleOutlined />,
                  content: `確定要取消此筆交易的授權嗎？\n訂單：${record.order_sn}\n金額：${formatAmount(record.amount)}`,
                  okText: '確認取消',
                  cancelText: '返回',
                  okType: 'danger',
                  onOk: () => handleCancelAuth(record)
                })
              }
            },
            {
              key: 'refund',
              icon: <RollbackOutlined />,
              label: '退款',
              danger: true,
              onClick: () => {
                Modal.confirm({
                  title: '確認退款',
                  icon: <ExclamationCircleOutlined />,
                  content: `確定要對此筆交易進行退款嗎？\n訂單：${record.order_sn}\n金額：${formatAmount(record.amount)}`,
                  okText: '確認退款',
                  cancelText: '返回',
                  okType: 'danger',
                  onOk: () => handleRefund(record)
                })
              }
            }
          )
        }
        return (
          <Dropdown menu={{ items: menuItems }} trigger={['click']}>
            <Button size='small' loading={!!loading}>
              操作 ▾
            </Button>
          </Dropdown>
        )
      }
    }
  ]

  return (
    <div>
      <Title level={2} style={{ marginBottom: 24, color: colors.textPrimary }}>
        交易記錄管理
      </Title>

      {/* 统计信息卡片 */}
      <StatsCard>
        <Row gutter={[16, 16]}>
          <Col xs={12} sm={6}>
            <Statistic
              title='總交易金額'
              value={stats.totalAmount}
              prefix={<DollarOutlined />}
              formatter={(value) => formatAmount(value)}
              valueStyle={{ color: colors.textPrimary }}
            />
          </Col>
          <Col xs={12} sm={6}>
            <Statistic
              title='已完成金額'
              value={stats.completedAmount}
              prefix={<DollarOutlined />}
              formatter={(value) => formatAmount(value)}
              valueStyle={{ color: '#52c41a' }}
            />
          </Col>
          <Col xs={12} sm={6}>
            <Statistic
              title='總記錄數'
              value={stats.totalCount}
              valueStyle={{ color: colors.textPrimary }}
            />
          </Col>
          <Col xs={12} sm={6}>
            <Statistic
              title='已完成記錄數'
              value={stats.completedCount}
              valueStyle={{ color: '#52c41a' }}
            />
          </Col>
        </Row>
      </StatsCard>

      {/* 筛选和搜索 */}
      <StyledCard>
        <FilterContainer>
          <FilterItem>
            <Search
              placeholder='搜索訂單號、客戶姓名、郵箱、電話、商家名稱、交易號、卡號後四位'
              allowClear
              enterButton={<SearchOutlined />}
              size='large'
              onSearch={handleSearch}
              onChange={(e) => {
                if (!e.target.value) {
                  handleSearch('')
                }
              }}
              style={{ width: '100%' }}
            />
          </FilterItem>
          <FilterItem>
            <Select
              placeholder='支付狀態'
              allowClear
              style={{ width: '100%' }}
              size='large'
              onChange={handleStatusChange}
              value={filters.status}
            >
              <Option value='pending'>待處理</Option>
              <Option value='completed'>已完成</Option>
              <Option value='failed'>失敗</Option>
              <Option value='refunded'>已退款</Option>
            </Select>
          </FilterItem>
          <FilterItem>
            <RangePicker
              placeholder={['開始日期', '結束日期']}
              style={{ width: '100%' }}
              size='large'
              onChange={handleDateRangeChange}
              format='YYYY-MM-DD'
            />
          </FilterItem>
          <Button
            icon={<ReloadOutlined />}
            onClick={() => {
              handleClearFilters()
              fetchData(1, pagination.pageSize)
            }}
            size='large'
          >
            重置
          </Button>
        </FilterContainer>

        {/* 交易记录表格 */}
        <AdminTable
          columns={columns}
          dataSource={data}
          rowKey='_id'
          loading={loading}
          pagination={{
            currentPage: pagination.current,
            totalItems: pagination.total,
            itemsPerPage: pagination.pageSize
          }}
          onChange={handleTableChange}
        />
      </StyledCard>
    </div>
  )
}

export default CreditPaymentsPage
